import axios from 'axios';
import { getApiTimeout } from '../config/apiConfig';
import { handleApiError } from '../config/apiConfig';
import { appConfig, isTokenRefreshEnabled } from '../config/appConfig';

const ENABLE_TOKEN_REFRESH = isTokenRefreshEnabled();

export interface ExchangeTokenRequest {
  code: string;
  codeVerifier: string;
}

export interface ExchangeTokenResponse {
  access_token?: string;
  id_token?: string;
  refresh_token?: string;
  expires_in?: number;
}

// Create a separate axios instance for token endpoints with credentials
// Timeout set to 75s — must be > backend read timeout (60s) so the backend
// always responds (success or 503) before axios cancels the request.
// This ensures the user sees a spinner rather than an abrupt error during
// Azure AD slowness.
const tokenApiClient = axios.create({
  baseURL: appConfig.API_BASE_URL,
  timeout: 75000,   // 75s — deliberately > backend 60s read timeout
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add request interceptor to log outgoing requests
tokenApiClient.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    const operation = error.config?.url?.includes('clear-refresh-token') ? 'CLEAR-REFRESH-TOKEN' :
                      error.config?.url?.includes('refresh-token') ? 'REFRESH-TOKEN' : 
                      error.config?.url?.includes('exchange-token') ? 'EXCHANGE-TOKEN' : 'TOKEN-API';
    handleApiError(error, operation);
    return Promise.reject(error);
  }
);

// Add response interceptor to log responses
tokenApiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const operation = error.config?.url?.includes('clear-refresh-token') ? 'CLEAR-REFRESH-TOKEN' :
                      error.config?.url?.includes('refresh-token') ? 'REFRESH-TOKEN' : 
                      error.config?.url?.includes('exchange-token') ? 'EXCHANGE-TOKEN' : 'TOKEN-API';
    handleApiError(error, operation);
      return Promise.reject(error);
    }
);

/**
 * Retry helper with exponential backoff.
 *
 * Strategy for Azure AD slowness:
 *   Attempt 1 → wait 2s → Attempt 2 → wait 4s → Attempt 3 → wait 8s → Attempt 4
 *   Total max wait before giving up: ~14s of retry delays + 4 x 75s axios timeout
 *   In practice Azure AD recovers within 1-2 retries.
 *
 * Only retries on TRANSIENT errors (timeout, 503, 504).
 * Does NOT retry on 400/401/403 — those are logic errors, not slowness.
 *
 * @param fn         Async function to retry
 * @param retries    Maximum number of additional attempts (default 3)
 * @param delayMs    Initial delay in ms before first retry (doubles each time)
 * @param onRetry    Optional callback invoked before each retry with attempt number and max
 */
async function withRetry<T>(
  fn: () => Promise<T>,
  retries: number = 3,
  delayMs: number = 2000,
  onRetry?: (attempt: number, maxAttempts: number) => void
): Promise<T> {
  const maxAttempts = retries;
  let attemptNumber = 0;

  const execute = async (remainingRetries: number, currentDelay: number): Promise<T> => {
    try {
      return await fn();
    } catch (error: any) {
      // Only retry on timeout or transient server errors.
      // Also retry 429 (Too Many Requests) — the backend rate limiter may
      // briefly return 429 during a login burst; waiting and retrying is
      // the correct behaviour rather than showing an error to the user.
      const isRetryable =
        error.code === 'ECONNABORTED' ||
        error.message?.includes('timeout') ||
        error.response?.status === 429 ||
        error.response?.status === 503 ||
        error.response?.status === 504;

      if (remainingRetries > 0 && isRetryable) {
        attemptNumber += 1;
        console.warn(
          `[EXCHANGE-TOKEN] Azure AD slow — retrying after ${currentDelay}ms (attempt ${attemptNumber} of ${maxAttempts})...`
        );
        if (onRetry) {
          onRetry(attemptNumber, maxAttempts);
        }
        await new Promise((resolve) => setTimeout(resolve, currentDelay));
        return execute(remainingRetries - 1, currentDelay * 2);
      }
      throw error;
    }
  };

  return execute(retries, delayMs);
}

export async function callExchangeTokenEndpoint(
  code: string,
  codeVerifier: string,
  onRetry?: (attempt: number, maxAttempts: number) => void
): Promise<ExchangeTokenResponse> {
  try {
    const requestBody: ExchangeTokenRequest = { code, codeVerifier };
    // Wrap in retry: under high load / Azure AD slowness the first attempt may timeout;
    // subsequent attempts on a warm connection succeed without showing an error to the user.
    const response = await withRetry(
      () => tokenApiClient.post<ExchangeTokenResponse>('/auth/exchange-token', requestBody),
      3,
      2000,
      onRetry
    );
    return response.data;
  } catch (error: any) {
    handleApiError(error, 'EXCHANGE-TOKEN');
    throw new Error(`Token exchange failed: ${error.message}`);
  }
}

// Token response interface
export interface TokenResponse {
  access_token: string;
  id_token: string;
  refreshtoken: string;
  expires_in: number;
  token_type: string;
}
// Refresh token request interface
export interface RefreshTokenRequest {
  refreshToken: string;
}

/**
 * Refresh tokens using the refresh endpoint
 * Note: The refreshToken parameter is kept for compatibility but not used.
 * The actual refresh token is retrieved from HTTPOnly cookie by the server.
 */
export const refreshTokens = async (refreshToken?: string): Promise<TokenResponse | null> => {
  if (!ENABLE_TOKEN_REFRESH) {
    console.log('[REFRESH-TOKEN] Token refresh disabled by configuration');
    return null;
  }

  try {
    // No payload needed - server gets refresh token from HTTPOnly cookie
    const response = await tokenApiClient.post<TokenResponse>('/auth/refresh-tokens');
    return response.data;

  } catch (error: any) {
    handleApiError(error, 'REFRESH-TOKEN');
    console.log('[REFRESH-TOKEN] Token refresh failed');
    return null;
  }
};

/**
 * Clear refresh token on logout
 */
export const clearRefreshToken = async (): Promise<boolean> => {
  try {
    await tokenApiClient.post('/auth/clear-refresh-token');
    return true;
  } catch (error: any) {
    handleApiError(error, 'CLEAR-REFRESH-TOKEN');
    return false;
  }
};

/**
 * Check if a token is expired or about to expire (within specified minutes)
 */
export const isTokenExpiredOrExpiring = (token: string, bufferMinutes: number = 5): boolean => {
  try {
    // Decode JWT token to get expiration
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    const payload = JSON.parse(jsonPayload);
    const exp = payload.exp;
    
    if (!exp) {
      console.warn('Token does not have expiration claim');
      return false;
    }

    const now = Math.floor(Date.now() / 1000);
    const timeUntilExpiration = exp - now;
    const bufferSeconds = bufferMinutes * 60;
    
    // Token is expired or expiring if time until expiration is <= buffer time
    const isExpiring = timeUntilExpiration <= bufferSeconds;
    
    if (isExpiring) {
      console.log(`Token expires in ${Math.max(0, timeUntilExpiration)} seconds (${Math.max(0, timeUntilExpiration / 60).toFixed(1)} minutes), which is <= ${bufferMinutes} minutes buffer`);
    }
    
    return isExpiring;
  } catch (error: any) {
    console.warn('[isTokenExpiredOrExpiring] Failed to parse token expiration:', {
    message: error.message,
    time: new Date().toISOString(),
  });
    return true; // Assume expired if we can't parse
  }
};

/**
 * Get token expiration time in seconds from now
 */
export const getTokenExpirationTime = (token: string): number | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    const payload = JSON.parse(jsonPayload);
    const exp = payload.exp;
    
    if (!exp) {
      return null;
    }

    const now = Math.floor(Date.now() / 1000);
    return exp - now;
  } catch (error) {
    console.error('Error getting token expiration time:', error);
    return null;
  }
};
